// Экспорт турнирной таблицы группы в PNG: ручной рендер на canvas
// по паттерну src/views/players/exportRatingImage.js (html2canvas в проекте нет)

import {
  COLORS, FONT_FAMILY, ensureFontsReady, canvasToBlob, triggerDownload, ellipsize,
} from '@/views/fnf-league/canvasShared'

const WIDTH = 1200
const PADDING = 48
const HEADER_HEIGHT = 170
const ROW_HEIGHT = 56
const TABLE_HEADER_HEIGHT = 44

export default async function exportLeagueTableImage({ season, group }) {
  await ensureFontsReady()

  const { rows } = group
  const height = HEADER_HEIGHT + TABLE_HEADER_HEIGHT + rows.length * ROW_HEIGHT + PADDING * 2

  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = height
  const ctx = canvas.getContext('2d')

  ctx.fillStyle = COLORS.background
  ctx.fillRect(0, 0, WIDTH, height)

  // Шапка
  ctx.fillStyle = COLORS.cardTop
  ctx.fillRect(0, 0, WIDTH, HEADER_HEIGHT)
  ctx.fillStyle = COLORS.headerText
  ctx.font = `bold 52px ${FONT_FAMILY}`
  ctx.textBaseline = 'middle'
  ctx.fillText('ФНФ ЛИГА', PADDING, 64)
  ctx.font = `32px ${FONT_FAMILY}`
  ctx.fillText(`Сезон ${season} · Группа ${group.group_letter}`, PADDING, 120)

  // Колонки: # | ± | Группа X / Игрок | Игр | 1м | 2м | 3м | 4м | Баллов | Сумма очков.
  // «±» — сдвиг места относительно таблицы без последнего тура; бэкенд присылает
  // place_change только когда сыграно >1 тура и у всех поровну игр — иначе колонки нет.
  // «Баллов» — основная колонка сортировки, выделяется подсветкой и акцентным цветом.
  // Числовые колонки прижаты к правому краю, имени достаётся всё остальное
  // (при WIDTH=1200: statsLeft = 1200 - 48 - 570 = 582, ширина имени 582 - 104 - 16 = 462px,
  // с колонкой «±» — на 64px меньше).
  const hasPlaceChanges = rows.some(row => row.place_change !== null && row.place_change !== undefined)
  const formatPlaceChange = change => {
    if (change > 0) return `+${change}`
    if (change < 0) return String(change)
    return '='
  }
  const placeChangeColor = change => {
    if (change > 0) return COLORS.up
    if (change < 0) return COLORS.down
    return COLORS.muted
  }
  const statColumns = [
    { label: 'Игр', width: 70, value: row => row.games_played, accent: false },
    { label: '1м', width: 60, value: row => row.first_places, accent: false },
    { label: '2м', width: 60, value: row => row.second_places, accent: false },
    { label: '3м', width: 60, value: row => row.third_places, accent: false },
    { label: '4м', width: 60, value: row => row.fourth_places, accent: false },
    { label: 'Баллов', width: 110, value: row => row.total_points, accent: true },
    { label: 'Сумма очков', width: 150, value: row => row.total_score, accent: false },
  ]
  const statsWidth = statColumns.reduce((sum, column) => sum + column.width, 0)
  const changeLeft = PADDING + 48
  const nameLeft = PADDING + 56 + (hasPlaceChanges ? 64 : 0)
  const statsLeft = WIDTH - PADDING - statsWidth
  const nameMaxWidth = statsLeft - nameLeft - 16

  // Подсветка колонки «Баллов» на всю высоту таблицы — рисуется под текстом
  let accentColumnLeft = statsLeft
  for (const column of statColumns) {
    if (column.accent) break
    accentColumnLeft += column.width
  }
  const accentColumn = statColumns.find(column => column.accent)
  const tableTop = HEADER_HEIGHT + PADDING / 2
  const tableBottom = tableTop + TABLE_HEADER_HEIGHT + rows.length * ROW_HEIGHT

  // Подсветка строк: 1-е место в каждой группе; в группе A — вся топ-4 (выход в плей-офф)
  const isHighlightedRow = row => row.place === 1 || (group.group_letter === 'A' && row.place <= 4)

  // Сначала фоны (зебра строк + лента подсветки), текст — поверх
  let y = tableTop + TABLE_HEADER_HEIGHT
  rows.forEach((row, index) => {
    if (isHighlightedRow(row)) {
      ctx.fillStyle = COLORS.rowHighlight
    } else {
      ctx.fillStyle = index % 2 === 0 ? COLORS.rowEven : COLORS.rowOdd
    }
    ctx.fillRect(PADDING / 2, y, WIDTH - PADDING, ROW_HEIGHT)
    y += ROW_HEIGHT
  })
  ctx.fillStyle = 'rgba(255, 209, 102, 0.10)'
  ctx.fillRect(accentColumnLeft - 14, tableTop, accentColumn.width, tableBottom - tableTop)

  ctx.font = `bold 22px ${FONT_FAMILY}`
  ctx.fillStyle = COLORS.muted
  ctx.fillText('#', PADDING, tableTop + TABLE_HEADER_HEIGHT / 2)
  if (hasPlaceChanges) ctx.fillText('±', changeLeft, tableTop + TABLE_HEADER_HEIGHT / 2)
  ctx.fillText(`Группа ${group.group_letter} / Игрок`, nameLeft, tableTop + TABLE_HEADER_HEIGHT / 2)
  let headerX = statsLeft
  statColumns.forEach(column => {
    ctx.fillStyle = column.accent ? COLORS.accent : COLORS.muted
    ctx.fillText(column.label, headerX, tableTop + TABLE_HEADER_HEIGHT / 2)
    headerX += column.width
  })

  y = tableTop + TABLE_HEADER_HEIGHT
  rows.forEach(row => {
    const middle = y + ROW_HEIGHT / 2
    ctx.fillStyle = row.place <= 3 ? COLORS.accent : COLORS.text
    ctx.font = `bold 26px ${FONT_FAMILY}`
    ctx.fillText(String(row.place), PADDING, middle)

    if (hasPlaceChanges && row.place_change !== null) {
      ctx.fillStyle = placeChangeColor(row.place_change)
      ctx.font = `bold 22px ${FONT_FAMILY}`
      ctx.fillText(formatPlaceChange(row.place_change), changeLeft, middle)
    }

    ctx.fillStyle = COLORS.text
    ctx.font = `26px ${FONT_FAMILY}`
    ctx.fillText(ellipsize(ctx, row.name, nameMaxWidth), nameLeft, middle)

    let cellX = statsLeft
    statColumns.forEach(column => {
      ctx.fillStyle = column.accent ? COLORS.accent : COLORS.muted
      ctx.font = column.accent ? `bold 26px ${FONT_FAMILY}` : `24px ${FONT_FAMILY}`
      ctx.fillText(String(column.value(row)), cellX, middle)
      cellX += column.width
    })

    y += ROW_HEIGHT
  })

  const blob = await canvasToBlob(canvas)
  triggerDownload(blob, `ФНФ_Лига_Сезон${season}_Группа_${group.group_letter}.png`)
}
